import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { DownOutlined } from '@ant-design/icons'
import { Button, Dropdown as AntDropdown, Menu } from 'antd'

const Dropdown = ({
  mode = 'multiple',
  value,
  values,
  children = null,
  onChange,
  title,
  titlePropName = 'title',
  trigger = ['click'],
  valuePropName = 'value',
  scrollable = false
}) => {
  const [visible, setVisible] = useState(false)
  // Set on mousedown (fires BEFORE click and onOpenChange) so we reliably know a
  // menu interaction is in progress, regardless of antd's event ordering. This
  // prevents the menu from closing on item select – which would otherwise expose
  // the table row underneath and let the same physical click navigate it.
  const interactingRef = useRef(false)

  const onClick = item => {
    // Prevent the click from bubbling to an underlying clickable table row
    item.domEvent?.stopPropagation()
    // Always use item.key since we explicitly set it in Menu.Item
    const filterProp = item.key

    // Toggle: if exists, remove; if not, add
    if (value.find(v => v === filterProp)) {
      onChange(value.filter(v => v !== filterProp))
    } else {
      onChange([...value, filterProp])
    }
    // NOTE: don't reset interactingRef here. antd fires this onClick BEFORE
    // onOpenChange, so resetting now would let the menu close on every pick.
    // The flag is reset inside handleVisibleChange's keep-open branch instead.
  }

  const handleVisibleChange = shouldBeVisible => {
    // Keep the dropdown open while interacting with menu items in multiple mode
    if (!shouldBeVisible && mode === 'multiple' && interactingRef.current) {
      interactingRef.current = false
      return // Don't close
    }
    setVisible(shouldBeVisible)
  }
  const menu = (
    <Menu
      onMouseDown={() => {
        interactingRef.current = true
      }}
      style={scrollable ? { maxHeight: '300px', overflowY: 'scroll' } : null}
      multiple={mode === 'multiple'}
      onClick={onClick}
      selectedKeys={value}
    >
      {children ||
        values?.map(item => (
          <Menu.Item key={item.key} value={item[valuePropName]}>
            {item[titlePropName]}
          </Menu.Item>
        ))}
    </Menu>
  )
  return (
    <AntDropdown
      overlay={menu}
      trigger={trigger}
      open={visible}
      onOpenChange={handleVisibleChange}
      getPopupContainer={() => document.body}
    >
      <Button
        onClick={e => {
          // Stop propagation so opening the filter does not navigate the row;
          // let AntDropdown's onOpenChange handle the actual open/close toggle.
          e.stopPropagation()
        }}
      >
        {title}
        <DownOutlined />
      </Button>
    </AntDropdown>
  )
}
Dropdown.propTypes = {
  mode: PropTypes.oneOf(['multiple']),
  value: PropTypes.oneOfType([PropTypes.array]).isRequired,
  values: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  titlePropName: PropTypes.string,
  valuePropName: PropTypes.string,
  children: PropTypes.node,
  trigger: PropTypes.array,
  scrollable: PropTypes.bool
}

export default Dropdown
